/**
 * Greek-aware uppercasing.
 *
 * CSS `text-transform: uppercase` is unusable for Greek: it keeps the tonos
 * (Αθήνα → ΑΘΉΝΑ) where correct Greek drops it (ΑΘΗΝΑ). So headings are
 * uppercased in JS and the CSS utility is neutralised under html[lang='el'].
 *
 * The subtlety the naive version misses: Greek drops the *tonos* but KEEPS the
 * *dialytika*. Μαΐου is ΜΑΪΟΥ, not ΜΑΙΟΥ — and stripping every combining mark
 * (the usual one-liner) silently loses that, plus mangles Latin text like
 * "Café" into "CAFE".
 */

// Greek and Coptic, plus Greek Extended for any polytonic input.
const GREEK = /[Ͱ-Ͽἀ-῿]/;

// Tonos / oxia, varia, perispomeni. Deliberately excludes U+0308 dialytika.
const GREEK_ACCENTS = /[̀́̀́͂̓̈́ͅ]/g;

const COMBINING_START = 0x0300;
const COMBINING_END = 0x036f;

/**
 * Uppercase a string using Greek capitalisation rules.
 * Non-Greek characters keep their own diacritics untouched.
 *
 * @param {string} input
 * @returns {string}
 */
function toGreekUpper(input) {
  if (input == null) return input;

  const decomposed = String(input).normalize('NFD');
  let out = '';
  let i = 0;

  while (i < decomposed.length) {
    const base = decomposed[i];
    i += 1;

    // Collect the combining marks attached to this base character.
    let marks = '';
    while (i < decomposed.length) {
      const code = decomposed.codePointAt(i);
      if (code >= COMBINING_START && code <= COMBINING_END) {
        marks += decomposed[i];
        i += 1;
      } else {
        break;
      }
    }

    if (marks && GREEK.test(base)) {
      // U+0344 (dialytika+tonos) decomposes to dialytika + tonos under NFD,
      // so dropping the accents here leaves the dialytika standing on its own.
      marks = marks.replace(GREEK_ACCENTS, '');
    }

    out += base + marks;
  }

  // Recompose first: ι + U+0308 must become ϊ so that toUpperCase yields Ϊ.
  // Final sigma is handled natively (ς → Σ).
  return out.normalize('NFC').toUpperCase();
}

/**
 * Locale-aware uppercase for UI labels.
 * English goes through the platform's own casing; Greek through the rules above.
 *
 * @param {string} input
 * @param {string} locale
 */
function upper(input, locale) {
  if (input == null) return input;
  return locale === 'el' ? toGreekUpper(input) : String(input).toUpperCase();
}

module.exports = { toGreekUpper, upper };
module.exports.default = toGreekUpper;
